import {AppLogo} from "@components/AppLogo";
import {PrevIcon} from "@components/icons";
import {IconButton, Stack, Typography} from "@mui/material";
import {translate} from "@util";
import React, {ReactNode} from "react";
import {DrawerItem as DrawerItemDto} from "@core/model";
import {DrawerItem} from "./DrawerItem";
import {useThemeContext} from "@theme";
import {useSelector} from "react-redux";
import {Selectors} from "@core/store/selectors";
import {AsyncImage, TenantSelector} from "@components";

const DrawerContent = ({
  items,
  selected,
  open,
  setOpen,
  onItemClick,
}: {
  items: (DrawerItemDto | ReactNode)[];
  selected: number;
  open: boolean | undefined;
  onItemClick: (index: number, path: string) => void;
  setOpen: (v: boolean) => void;
}) => {
  const {theme} = useThemeContext();
  const tenant = useSelector(Selectors.tenant);
  const userRole = useSelector(Selectors.userRole);

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1} my={1}>
        <AppLogo
          containerSx={{ml: 1}}
          logoSize={40}
          variant="h6"
          onClick={() => setOpen(!open)}
        />
        <IconButton onClick={() => setOpen(false)} size="small">
          <PrevIcon fontSize="inherit" />
        </IconButton>
      </Stack>

      <Stack
        gap={1}
        sx={{
          flex: "1 1 0",
          overflowX: "hidden",
          overflowY: "auto",
          "& .drawer-section": {
            mx: 2,
            mt: 1,
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          },
          "& .drawer-section .MuiTypography-root": {
            display: open ? "block" : "none",
          },
          "& .drawer-section:before": {
            content: `""`,
            blockSize: 1,
            backgroundColor: theme.palette.divider,
            ...(open ? {inlineSize: "0.875rem"} : {flex: 1}),
          },
          ...(open && {
            "& .drawer-section:after": {
              content: `""`,
              blockSize: 1,
              backgroundColor: theme.palette.divider,
              flex: 1,
            },
          }),
        }}>
        {items.map((elem, index) => {
          if ((elem as DrawerItemDto).path !== undefined) {
            const item = elem as DrawerItemDto;
            const label = item.i18n ? translate(item.i18n) : item.name;
            return (
              <DrawerItem
                key={"dashboar_list_item_" + index}
                item={item}
                drawerOpen={open}
                label={label}
                selected={index === selected}
                onClick={() => onItemClick(index, item.path)}
              />
            );
          } else {
            return (
              <React.Fragment key={"dashboar_item_" + index}>
                {elem as ReactNode}
              </React.Fragment>
            );
          }
        })}
      </Stack>

      {open && (
        <Stack justifyContent={"end"} alignItems={"center"} my={1}>
          {userRole === "ADMIN" ? (
            <TenantSelector />
          ) : (
            <AsyncImage
              id={tenant?.img}
              size="contain"
              height={40}
              fallbackNode={
                <Typography
                  variant={"h5"}
                  align="center"
                  color={theme.palette.common.white}>
                  {tenant?.name}
                </Typography>
              }
              width={200}
            />
          )}
        </Stack>
      )}
    </>
  );
};

export {DrawerContent};
